<script lang="ts">
  /**
   * Author or edit a manual board — one form for both, as in `AdminAutomations`.
   *
   * COLUMNS CARRY THEIR KEY THROUGH AN EDIT. A column the pipeline already declares is
   * submitted with the `key` the server gave it, which is what makes a rename a rename:
   * drop the key and the server reads a delete plus an insert, and every card filed in
   * that column would be stranded pointing at a state that no longer exists. Only a
   * newly added row goes up without one.
   *
   * THE ROW ORDER IS THE BOARD'S COLUMN ORDER, left to right, so rows can be dragged.
   * Without that, a column in the wrong place could only be corrected by deleting it and
   * adding it back — which mints a NEW key, and so strands every card filed in it.
   * Reordering moves the whole row, key included, and renames nothing.
   */
  import type { Pipeline, StageDraft, StageKind } from '@api';
  import { STAGE_KIND_META } from '../pipelines';

  interface Props {
    mode: 'create' | 'edit';
    /** The board being edited. Ignored when `mode === 'create'`. */
    initial?: Pipeline | null;
    busy?: boolean;
    error?: string | null;
    onsave: (name: string, stages: StageDraft[]) => void;
    oncancel: () => void;
  }

  const { mode, initial = null, busy = false, error = null, onsave, oncancel }: Props = $props();

  /** A row in the editor. `uid` is local and never sent — it exists so the `{#each}` can
   *  be keyed by identity rather than by index. Keyed by index, a reorder leaves every
   *  input bound to whichever row USED to sit in that position: drag the third column to
   *  the top and the labels stay put while the colours appear to shuffle. */
  type Row = { uid: number; key?: string; label: string; kind: StageKind };

  let nextUid = 0;
  const asRow = (s: StageDraft): Row => ({ ...s, uid: nextUid++ });

  // A starting board that is already useful: two working columns and the two outcomes.
  // An empty form would make the first thing an owner does be inventing a vocabulary.
  const BLANK: StageDraft[] = [
    { label: 'To do', kind: 'in_progress' },
    { label: 'In progress', kind: 'in_progress' },
    { label: 'Done', kind: 'won' },
    { label: 'Dropped', kind: 'lost' },
  ];

  // SEEDED ONCE, ON PURPOSE. The dialog is mounted fresh each time it opens (the page
  // renders it under `{#if form}`), so the props are a starting point for an editable
  // draft rather than a live source — tracking them would overwrite what someone is
  // halfway through typing the moment the board list refreshed underneath.
  // svelte-ignore state_referenced_locally
  let name = $state(initial?.name ?? '');
  // svelte-ignore state_referenced_locally
  let stages = $state<Row[]>(
    (initial
      ? initial.stages.map((s) => ({ key: s.key, label: s.label, kind: s.kind }))
      : BLANK
    ).map(asRow),
  );

  const KINDS: { value: StageKind; label: string }[] = [
    { value: 'in_progress', label: 'Working' },
    { value: 'won', label: 'Won' },
    { value: 'lost', label: 'Lost' },
  ];

  const MAX_STAGES = 12;
  const filled = $derived(stages.filter((s) => s.label.trim().length > 0));
  const hasWorking = $derived(filled.some((s) => s.kind === 'in_progress'));
  const ready = $derived(
    name.trim().length > 0 && filled.length > 0 && hasWorking && !busy,
  );

  function addStage(): void {
    if (stages.length >= MAX_STAGES) return;
    stages = [...stages, asRow({ label: '', kind: 'in_progress' })];
  }

  function removeStage(uid: number): void {
    stages = stages.filter((s) => s.uid !== uid);
  }

  // --- reordering --------------------------------------------------------------------
  // Hand-rolled HTML5 drag events, as `PipelineBoard` does for cards. The app carries no
  // drag-and-drop library and a four-row list is not the reason to introduce one.

  let dragFrom = $state<number | null>(null);
  let dragOver = $state<number | null>(null);
  /** Which row is draggable right now. Toggled by the GRIP rather than left true on the
   *  row: a permanently draggable row cannot have its text selected, so click-dragging
   *  inside the label input would pick the whole row up instead of selecting a word. */
  let grabbed = $state<number | null>(null);

  function move(from: number, to: number): void {
    if (from === to) return;
    if (from < 0 || to < 0 || from >= stages.length || to >= stages.length) return;
    const next = [...stages];
    const [moved] = next.splice(from, 1);
    if (moved) next.splice(to, 0, moved);
    stages = next;
  }

  function dragStart(e: DragEvent, i: number): void {
    dragFrom = i;
    // Firefox begins no drag at all without a payload, even one nothing ever reads.
    e.dataTransfer?.setData('text/plain', String(i));
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
  }

  function dragOverRow(e: DragEvent, i: number): void {
    if (dragFrom === null) return;
    e.preventDefault(); // the DEFAULT is to refuse the drop
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    dragOver = i;
  }

  function drop(e: DragEvent, i: number): void {
    e.preventDefault();
    if (dragFrom !== null) move(dragFrom, i);
    endDrag();
  }

  function endDrag(): void {
    dragFrom = null;
    dragOver = null;
    grabbed = null;
  }

  /** The same reorder from the keyboard. The grip is a real button and takes focus, so
   *  ↑/↓ move the row — a control that only answers to a dragged mouse is one a keyboard
   *  user does not have at all. */
  function gripKey(e: KeyboardEvent, i: number): void {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    e.preventDefault();
    move(i, e.key === 'ArrowUp' ? i - 1 : i + 1);
  }

  function submit(): void {
    if (!ready) return;
    onsave(
      name.trim(),
      // Blank rows are dropped rather than refused: an owner who added a row and changed
      // their mind should not have to find the remove button to be allowed to save.
      //
      // `key` is OMITTED rather than sent as undefined for a new column — under
      // `exactOptionalPropertyTypes` those are different things, and the server reads a
      // present-but-empty key as an existing column it cannot find. `uid` never travels.
      filled.map((s) =>
        s.key
          ? { key: s.key, label: s.label.trim(), kind: s.kind }
          : { label: s.label.trim(), kind: s.kind },
      ),
    );
  }

  function onkeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') oncancel();
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit();
  }
</script>

<svelte:window on:keydown={onkeydown} />

<div class="fixed inset-0 z-[90] grid place-items-center bg-black/45 p-4"
     role="dialog" aria-modal="true" aria-label={mode === 'edit' ? 'Edit pipeline' : 'New pipeline'}>
  <button class="absolute inset-0" aria-label="Cancel" onclick={oncancel}></button>

  <div class="hair relative w-full max-w-lg rounded-box border bg-base-100 p-5 shadow-2xl">
    <p class="eyebrow">{mode === 'edit' ? 'Edit pipeline' : 'New pipeline'}</p>
    <!-- Only when creating. On an edit the reader has been using this board; telling
         them what it is again is an explanation nobody asked for in the one place they
         came to change something. -->
    {#if mode === 'create'}
      <p class="mt-1 text-sm text-base-content/72">
        A board you fill yourself. Nothing is added or moved automatically — members
        appear here only when you put them here.
      </p>
    {/if}

    <label class="mt-4 flex flex-col">
      <span class="label-text mb-1 text-xs font-medium text-base-content/60">Name</span>
      <!-- privacy-ok: the owner naming their own board, not a value we hold about a member -->
      <input class="input input-bordered input-sm" bind:value={name} maxlength="120"
             placeholder="Coaching track" />
    </label>

    <div class="mt-4 flex flex-col gap-2">
      <div class="flex items-baseline justify-between">
        <span class="text-xs font-medium text-base-content/60">Columns</span>
        <span class="text-[0.7rem] text-base-content/45">drag to reorder — left to right on the board</span>
      </div>

      {#each stages as stage, i (stage.uid)}
        <div
          role="listitem"
          class="flex items-center gap-2 rounded-field transition-colors
                 {dragOver === i && dragFrom !== i ? 'bg-primary/[0.07] ring-1 ring-primary/45' : ''}
                 {dragFrom === i ? 'opacity-40' : ''}"
          draggable={grabbed === i}
          ondragstart={(e) => dragStart(e, i)}
          ondragover={(e) => dragOverRow(e, i)}
          ondrop={(e) => drop(e, i)}
          ondragend={endDrag}
        >
          <!-- The GRIP turns dragging on, not the row — see `grabbed`. `touch-none` stops
               a touch drag scrolling the dialog instead of moving the row. -->
          <button
            type="button"
            class="shrink-0 cursor-grab touch-none rounded px-0.5 leading-none text-base-content/35 transition-colors hover:text-base-content focus-visible:ring-1 focus-visible:ring-primary/40 active:cursor-grabbing"
            aria-label="Reorder {stage.label || 'this column'}: drag, or press the up and down arrows"
            title="Drag to reorder — or focus and press ↑ / ↓"
            onpointerdown={() => (grabbed = i)}
            onpointerup={() => (grabbed = null)}
            onkeydown={(e) => gripKey(e, i)}
          >⠿</button>

          <span class="size-2 shrink-0 rounded-full {STAGE_KIND_META[stage.kind].dot}"></span>
          <!-- privacy-ok: the owner naming their own column -->
          <input class="input input-bordered input-xs flex-1" bind:value={stage.label}
                 maxlength="60" placeholder="Column name" />
          <select class="select select-bordered select-xs w-24" bind:value={stage.kind}>
            {#each KINDS as k}<option value={k.value}>{k.label}</option>{/each}
          </select>
          <button class="btn btn-ghost btn-xs" onclick={() => removeStage(stage.uid)}
                  disabled={stages.length <= 1} aria-label="Remove column">✕</button>
        </div>
      {/each}

      {#if stages.length < MAX_STAGES}
        <button class="btn btn-ghost btn-xs self-start" onclick={addStage}>+ Add column</button>
      {/if}

      {#if !hasWorking && filled.length > 0}
        <p class="text-[0.7rem] text-warning">
          Keep at least one Working column — that is where cards live before they are settled.
        </p>
      {/if}
    </div>

    {#if error}<p class="mt-3 text-sm text-error">{error}</p>{/if}

    <div class="mt-5 flex justify-end gap-2">
      <button class="btn btn-ghost btn-sm" onclick={oncancel}>Cancel</button>
      <button class="btn btn-primary btn-sm" onclick={submit} disabled={!ready}>
        {#if busy}<span class="loading loading-spinner loading-xs"></span>{/if}
        {mode === 'edit' ? 'Save' : 'Create board'}
      </button>
    </div>
  </div>
</div>
